import React, { useState, useEffect, useRef } from 'react';
import { FiCalendar, FiUsers, FiGift, FiCheckCircle, FiShield, FiClock, FiMessageCircle, FiSend, FiHeart, FiCamera, FiStar, FiArrowRight, FiPrinter, FiDownload } from 'react-icons/fi';

/* ═══════════════════════════════════════════
   GRAND GATHER REGISTRY — INNOVATION BROCHURE
   Competition pitch / showcase page
   ═══════════════════════════════════════════ */

const C = {
  coral: '#E3425C',
  coralDark: '#D13A4F',
  charcoal: '#0F0F11',
  charcoalMid: '#1A1A1E',
  gold: '#C9A84C',
  goldLight: '#E8D59A',
  cream: '#FAF8F5',
  sage: '#5C7A5A',
};

function useInView(threshold = 0.15) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setInView(true); obs.disconnect(); } }, { threshold });
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, inView];
}

function Reveal({ children, delay = 0, direction = 'up' }) {
  const [ref, inView] = useInView();
  const transforms = { up: 'translateY(40px)', down: 'translateY(-40px)', left: 'translateX(40px)', right: 'translateX(-40px)', scale: 'scale(0.92)' };
  return (
    <div ref={ref} style={{
      opacity: inView ? 1 : 0,
      transform: inView ? 'none' : transforms[direction],
      transition: `opacity 0.9s cubic-bezier(0.16,1,0.3,1) ${delay}s, transform 0.9s cubic-bezier(0.16,1,0.3,1) ${delay}s`,
    }}>
      {children}
    </div>
  );
}

/* Phone mockup frame */
function PhoneMockup({ children, style = {}, rotate = 0, scale = 1 }) {
  return (
    <div style={{
      width: '280px', height: '560px',
      borderRadius: '36px',
      background: '#000',
      padding: '12px',
      boxShadow: '0 30px 80px rgba(0,0,0,0.4), 0 0 0 1px rgba(255,255,255,0.08)',
      transform: `rotate(${rotate}deg) scale(${scale})`,
      flexShrink: 0,
      position: 'relative',
      ...style,
    }}>
      {/* Notch */}
      <div style={{
        position: 'absolute', top: '12px', left: '50%', transform: 'translateX(-50%)',
        width: '120px', height: '28px', background: '#000', borderRadius: '0 0 18px 18px', zIndex: 10,
      }} />
      <div style={{
        width: '100%', height: '100%', borderRadius: '24px', overflow: 'hidden',
        background: C.cream, position: 'relative',
      }}>
        {children}
      </div>
    </div>
  );
}

/* Feature pill */
function FeaturePill({ icon, label, color = C.coral }) {
  return (
    <div style={{
      display: 'inline-flex', alignItems: 'center', gap: '8px',
      padding: '10px 20px', borderRadius: '50px',
      background: `${color}12`, border: `1px solid ${color}25`,
      fontSize: '13px', fontWeight: 600, color,
      fontFamily: "'Instrument Sans', sans-serif",
    }}>
      {icon} {label}
    </div>
  );
}

/* Stat counter */
function StatBlock({ number, label, color = 'white' }) {
  return (
    <div style={{ textAlign: 'center' }}>
      <div style={{
        fontFamily: "'Playfair Display', serif",
        fontSize: 'clamp(36px, 5vw, 56px)', fontWeight: 700, color,
        lineHeight: 1,
      }}>{number}</div>
      <div style={{
        fontSize: '13px', color: `${color}99`, marginTop: '6px',
        fontFamily: "'Instrument Sans', sans-serif", letterSpacing: '1px', textTransform: 'uppercase',
      }}>{label}</div>
    </div>
  );
}

export default function Brochure() {
  const [currentSlide, setCurrentSlide] = useState(0);

  return (
    <div style={{
      background: C.charcoal, minHeight: '100vh', overflow: 'hidden',
      fontFamily: "'Instrument Sans', sans-serif",
    }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,600;0,700;0,800;1,400;1,600&family=Instrument+Sans:wght@400;500;600;700&display=swap');

        .brochure-page * { box-sizing: border-box; margin: 0; padding: 0; }

        .brochure-slide {
          min-height: 100vh;
          position: relative;
          overflow: hidden;
        }

        .print-btn {
          position: fixed; top: 20px; right: 20px; z-index: 100;
          display: flex; gap: 8px;
        }
        .print-btn button {
          padding: 10px 18px; border-radius: 8px; border: 1px solid rgba(255,255,255,0.15);
          background: rgba(0,0,0,0.6); backdrop-filter: blur(12px);
          color: white; font-size: 13px; font-weight: 600; cursor: pointer;
          display: flex; align-items: center; gap: 6px;
          font-family: 'Instrument Sans', sans-serif;
          transition: all 0.2s;
        }
        .print-btn button:hover { background: rgba(255,255,255,0.1); }

        @media print {
          .print-btn { display: none !important; }
          .brochure-slide { page-break-after: always; }
        }

        @keyframes floatSlow { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-20px); } }
        @keyframes floatMed { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-14px); } }
        @keyframes pulse { 0%, 100% { opacity: 0.3; } 50% { opacity: 0.7; } }
        @keyframes shimmer { 0% { background-position: -200% center; } 100% { background-position: 200% center; } }
        @keyframes rotateSlow { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
        @keyframes slideUp { from { opacity: 0; transform: translateY(60px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes scaleIn { from { opacity: 0; transform: scale(0.8); } to { opacity: 1; transform: scale(1); } }
      `}</style>

      <div className="brochure-page">

        {/* Print/Download buttons */}
        <div className="print-btn">
          <button onClick={() => window.print()}><FiPrinter size={14} /> Print / PDF</button>
        </div>

        {/* ══════════════════════════════════════
            SLIDE 1: HERO — THE VISION
            ══════════════════════════════════════ */}
        <section className="brochure-slide" style={{
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          padding: '60px 32px', textAlign: 'center',
          background: `radial-gradient(ellipse at 30% 20%, rgba(227,66,92,0.12) 0%, transparent 50%),
                       radial-gradient(ellipse at 70% 80%, rgba(201,168,76,0.08) 0%, transparent 50%),
                       ${C.charcoal}`,
        }}>
          {/* Decorative orbits */}
          <div style={{
            position: 'absolute', top: '50%', left: '50%', width: '600px', height: '600px',
            border: '1px solid rgba(255,255,255,0.03)', borderRadius: '50%',
            transform: 'translate(-50%, -50%)', animation: 'rotateSlow 60s linear infinite',
          }}>
            <div style={{ position: 'absolute', top: '-4px', left: '50%', width: '8px', height: '8px', borderRadius: '50%', background: C.coral, opacity: 0.6 }} />
          </div>
          <div style={{
            position: 'absolute', top: '50%', left: '50%', width: '400px', height: '400px',
            border: '1px solid rgba(255,255,255,0.04)', borderRadius: '50%',
            transform: 'translate(-50%, -50%)', animation: 'rotateSlow 40s linear reverse infinite',
          }}>
            <div style={{ position: 'absolute', top: '-3px', left: '50%', width: '6px', height: '6px', borderRadius: '50%', background: C.gold, opacity: 0.5 }} />
          </div>

          <Reveal>
            <img src="/gift-icon-v2.png" alt="GGR" style={{
              height: '120px', marginBottom: '24px',
              filter: 'drop-shadow(0 8px 30px rgba(227,66,92,0.3))',
            }} />
          </Reveal>

          <Reveal delay={0.1}>
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: '8px',
              padding: '8px 20px', borderRadius: '50px',
              background: 'rgba(227,66,92,0.1)', border: '1px solid rgba(227,66,92,0.2)',
              marginBottom: '28px',
            }}>
              <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: C.coral, animation: 'pulse 2s ease-in-out infinite' }} />
              <span style={{ fontSize: '12px', color: C.coral, fontWeight: 600, letterSpacing: '2px', textTransform: 'uppercase' }}>Innovation Showcase</span>
            </div>
          </Reveal>

          <Reveal delay={0.2}>
            <h1 style={{
              fontFamily: "'Playfair Display', serif",
              fontSize: 'clamp(42px, 7vw, 80px)',
              fontWeight: 700, lineHeight: 1.05,
              color: 'white', letterSpacing: '-0.02em',
              maxWidth: '800px', marginBottom: '20px',
            }}>
              Grand Gather<br />
              <span style={{
                backgroundImage: `linear-gradient(135deg, ${C.coral}, ${C.gold}, ${C.coral})`,
                backgroundSize: '200% auto',
                WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
                animation: 'shimmer 4s linear infinite',
              }}>Registry</span>
            </h1>
          </Reveal>

          <Reveal delay={0.3}>
            <p style={{
              fontSize: 'clamp(16px, 2vw, 20px)', lineHeight: 1.7,
              color: 'rgba(255,255,255,0.5)', maxWidth: '600px', marginBottom: '40px',
            }}>
              South Africa's smartest event planning and gift registry platform.
              Plan celebrations, manage guests, track RSVPs, and connect with vendors — all in one beautiful app.
            </p>
          </Reveal>

          <Reveal delay={0.4}>
            <div style={{ display: 'flex', gap: '40px', flexWrap: 'wrap', justifyContent: 'center' }}>
              <StatBlock number="9+" label="Event Types" />
              <StatBlock number="20+" label="Smart Features" />
              <StatBlock number="100%" label="South African" />
            </div>
          </Reveal>

          <Reveal delay={0.5}>
            <div style={{
              marginTop: '48px', fontSize: '13px', color: 'rgba(255,255,255,0.25)',
              letterSpacing: '3px', textTransform: 'uppercase',
            }}>
              Seamless Events, Thoughtful Gifting
            </div>
          </Reveal>
        </section>

        {/* ══════════════════════════════════════
            SLIDE 2: THE PROBLEM WE SOLVE
            ══════════════════════════════════════ */}
        <section className="brochure-slide" style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: '80px 32px',
          background: C.cream,
        }}>
          <div style={{ maxWidth: '1100px', width: '100%' }}>
            <Reveal>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
                <div style={{ width: '40px', height: '2px', background: C.coral }} />
                <span style={{ fontSize: '12px', fontWeight: 700, color: C.coral, letterSpacing: '3px', textTransform: 'uppercase' }}>The Problem</span>
              </div>
            </Reveal>

            <Reveal delay={0.1}>
              <h2 style={{
                fontFamily: "'Playfair Display', serif",
                fontSize: 'clamp(30px, 4vw, 48px)', fontWeight: 700,
                color: C.charcoal, lineHeight: 1.15, marginBottom: '48px',
                maxWidth: '700px',
              }}>
                Event planning in South Africa is <span style={{ color: C.coral, fontStyle: 'italic' }}>scattered, stressful</span>, and disconnected.
              </h2>
            </Reveal>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '20px' }}>
              {[
                { emoji: '📱', title: 'WhatsApp Chaos', desc: 'Guest lists scattered across 10 WhatsApp groups. RSVPs lost in chat. No way to track who\'s coming.' },
                { emoji: '📋', title: 'Excel Nightmares', desc: 'Budgets on spreadsheets, gift tracking on paper, vendor contacts in notebooks. Nothing connects.' },
                { emoji: '💸', title: 'Vendor Discovery Gap', desc: 'Finding trusted caterers, photographers, and DJs means asking friends and hoping for the best.' },
                { emoji: '🎁', title: 'Gift Duplication', desc: 'No central gift registry means duplicate gifts, awkward cash requests, and no tracking.' },
              ].map((item, i) => (
                <Reveal key={i} delay={0.15 + i * 0.08}>
                  <div style={{
                    padding: '28px', borderRadius: '16px',
                    background: 'white', border: '1px solid rgba(0,0,0,0.05)',
                    boxShadow: '0 2px 12px rgba(0,0,0,0.03)',
                    transition: 'transform 0.3s, box-shadow 0.3s',
                  }}>
                    <div style={{ fontSize: '32px', marginBottom: '14px' }}>{item.emoji}</div>
                    <h3 style={{ fontSize: '17px', fontWeight: 700, color: C.charcoal, marginBottom: '8px' }}>{item.title}</h3>
                    <p style={{ fontSize: '14px', lineHeight: 1.7, color: '#777' }}>{item.desc}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        {/* ══════════════════════════════════════
            SLIDE 3: OUR SOLUTION — FEATURE SHOWCASE
            ══════════════════════════════════════ */}
        <section className="brochure-slide" style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: '80px 32px',
          background: `linear-gradient(180deg, ${C.charcoal} 0%, ${C.charcoalMid} 100%)`,
        }}>
          <div style={{ maxWidth: '1100px', width: '100%' }}>
            <div style={{ textAlign: 'center', marginBottom: '56px' }}>
              <Reveal>
                <span style={{ fontSize: '12px', fontWeight: 700, color: C.gold, letterSpacing: '3px', textTransform: 'uppercase' }}>Our Solution</span>
              </Reveal>
              <Reveal delay={0.1}>
                <h2 style={{
                  fontFamily: "'Playfair Display', serif",
                  fontSize: 'clamp(30px, 4vw, 48px)', fontWeight: 700,
                  color: 'white', lineHeight: 1.15, marginTop: '14px',
                }}>
                  One Platform. <span style={{ color: C.coral }}>Every Celebration.</span>
                </h2>
              </Reveal>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '16px' }}>
              {[
                { icon: <FiCalendar size={22} />, title: 'Event Pages', desc: 'Beautiful custom event pages for weddings, lobola, birthdays, funerals and more', color: C.coral },
                { icon: <FiUsers size={22} />, title: 'Guest & RSVP', desc: 'Digital invitations, real-time RSVP tracking, dietary needs, plus-ones', color: '#3B82F6' },
                { icon: <FiGift size={22} />, title: 'Gift Registry', desc: 'Physical gifts, cash contributions, store gift codes with commission tracking', color: C.gold },
                { icon: <FiCheckCircle size={22} />, title: 'Digital Tickets', desc: 'Auto-generated QR tickets on RSVP with WhatsApp sharing', color: '#10B981' },
                { icon: <FiShield size={22} />, title: 'QR Check-in', desc: 'Scan tickets at the door for seamless guest verification', color: '#8B5CF6' },
                { icon: <FiMessageCircle size={22} />, title: 'Event Wall', desc: 'Interactive social wall for announcements, photos, reactions', color: '#EC4899' },
                { icon: <FiCamera size={22} />, title: 'Gallery', desc: 'Shared photo & video gallery with multiple layout styles', color: '#F59E0B' },
                { icon: <FiStar size={22} />, title: 'GiGi AI Assistant', desc: 'AI-powered event planning in 7 SA languages including isiZulu & Setswana', color: '#06B6D4' },
                { icon: <FiClock size={22} />, title: 'Smart Timeline', desc: 'Day-of programme with countdown timers and auto-reminders', color: C.sage },
                { icon: <FiHeart size={22} />, title: 'Thank You Cards', desc: '10 beautiful card styles auto-sent to guests after the event', color: '#EF4444' },
                { icon: <FiSend size={22} />, title: 'Auto Messaging', desc: 'Scheduled WhatsApp & email reminders for guests', color: '#14B8A6' },
                { icon: <FiArrowRight size={22} />, title: 'Vendor Marketplace', desc: 'Connect with caterers, photographers, DJs — request quotes in-app', color: '#F97316' },
              ].map((f, i) => (
                <Reveal key={i} delay={0.05 + i * 0.04}>
                  <div style={{
                    padding: '24px 20px', borderRadius: '14px',
                    background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)',
                    transition: 'all 0.3s',
                  }}>
                    <div style={{
                      width: '42px', height: '42px', borderRadius: '12px',
                      background: `${f.color}18`, color: f.color,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      marginBottom: '14px',
                    }}>{f.icon}</div>
                    <h3 style={{ fontSize: '15px', fontWeight: 700, color: 'white', marginBottom: '6px' }}>{f.title}</h3>
                    <p style={{ fontSize: '12px', lineHeight: 1.6, color: 'rgba(255,255,255,0.4)' }}>{f.desc}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        {/* ══════════════════════════════════════
            SLIDE 4: CULTURAL RELEVANCE — SA EVENTS
            ══════════════════════════════════════ */}
        <section className="brochure-slide" style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: '80px 32px',
          background: C.cream,
        }}>
          <div style={{ maxWidth: '1100px', width: '100%' }}>
            <div style={{ textAlign: 'center', marginBottom: '56px' }}>
              <Reveal>
                <span style={{ fontSize: '12px', fontWeight: 700, color: C.sage, letterSpacing: '3px', textTransform: 'uppercase' }}>Built for Africa</span>
              </Reveal>
              <Reveal delay={0.1}>
                <h2 style={{
                  fontFamily: "'Playfair Display', serif",
                  fontSize: 'clamp(30px, 4vw, 48px)', fontWeight: 700,
                  color: C.charcoal, lineHeight: 1.15, marginTop: '14px',
                }}>
                  Every celebration, <span style={{ color: C.coral, fontStyle: 'italic' }}>every culture.</span>
                </h2>
              </Reveal>
              <Reveal delay={0.15}>
                <p style={{ fontSize: '16px', color: '#888', maxWidth: '500px', margin: '16px auto 0' }}>
                  Purpose-built for South African celebrations with culturally-aware AI assistance
                </p>
              </Reveal>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: '14px' }}>
              {[
                { type: 'Wedding', emoji: '💍', color: '#E8A0BF', bg: '#2D1F2B' },
                { type: 'Lobola', emoji: '🤝', color: '#D4A574', bg: '#3B1F0B' },
                { type: 'Umembeso', emoji: '🎀', color: '#8FBC8F', bg: '#1A2E1A' },
                { type: 'Birthday', emoji: '🎂', color: '#FFB347', bg: '#1A1A3E' },
                { type: 'Funeral', emoji: '🕊️', color: '#9E9E9E', bg: '#1A1A1A' },
                { type: 'Graduation', emoji: '🎓', color: '#4FC3F7', bg: '#0D1B2A' },
                { type: 'Gender Reveal', emoji: '🎉', color: '#DDA0DD', bg: '#2B1F3D' },
                { type: 'Matric Dance', emoji: '💃', color: '#C084FC', bg: '#1A0A2E' },
                { type: 'Corporate', emoji: '🏢', color: '#38BDF8', bg: '#0F172A' },
                { type: 'Memorial', emoji: '🌹', color: '#A0A0A0', bg: '#1A1A1A' },
                { type: 'Baby Shower', emoji: '👶', color: '#FFB6C1', bg: '#2D1F2B' },
                { type: 'Engagement', emoji: '💐', color: '#FF6B6B', bg: '#2D1F2B' },
              ].map((e, i) => (
                <Reveal key={i} delay={0.05 + i * 0.03}>
                  <div style={{
                    padding: '24px 16px', borderRadius: '14px', textAlign: 'center',
                    background: e.bg, border: `1px solid ${e.color}22`,
                    transition: 'transform 0.3s',
                  }}>
                    <div style={{ fontSize: '28px', marginBottom: '10px' }}>{e.emoji}</div>
                    <div style={{ fontSize: '13px', fontWeight: 700, color: e.color }}>{e.type}</div>
                  </div>
                </Reveal>
              ))}
            </div>

            <Reveal delay={0.4}>
              <div style={{
                marginTop: '48px', padding: '28px 32px', borderRadius: '16px',
                background: `linear-gradient(135deg, ${C.charcoal}, ${C.charcoalMid})`,
                display: 'flex', alignItems: 'center', gap: '24px', flexWrap: 'wrap', justifyContent: 'center',
              }}>
                <img src="/gigi-mascot.png" alt="GiGi" style={{ width: '56px', height: '56px', borderRadius: '50%', border: `2px solid ${C.coral}33`, objectFit: 'contain' }} />
                <div style={{ flex: 1, minWidth: '240px' }}>
                  <h3 style={{ fontSize: '18px', fontWeight: 700, color: 'white', marginBottom: '4px' }}>Meet GiGi — Your AI Event Planner</h3>
                  <p style={{ fontSize: '14px', color: 'rgba(255,255,255,0.5)', lineHeight: 1.6 }}>
                    GiGi speaks <strong style={{ color: C.gold }}>7 South African languages</strong> — English, isiZulu, Setswana, Sepitori, Sesotho, isiXhosa, and Afrikaans.
                    She generates checklists, budgets, timelines, descriptions, and thank you messages tailored to each event type and culture.
                  </p>
                </div>
              </div>
            </Reveal>
          </div>
        </section>

        {/* ══════════════════════════════════════
            SLIDE 5: MONETIZATION / BUSINESS MODEL
            ══════════════════════════════════════ */}
        <section className="brochure-slide" style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: '80px 32px',
          background: `linear-gradient(135deg, ${C.charcoal} 0%, #1a1020 100%)`,
        }}>
          <div style={{ maxWidth: '1000px', width: '100%' }}>
            <div style={{ textAlign: 'center', marginBottom: '56px' }}>
              <Reveal>
                <span style={{ fontSize: '12px', fontWeight: 700, color: C.gold, letterSpacing: '3px', textTransform: 'uppercase' }}>Revenue Model</span>
              </Reveal>
              <Reveal delay={0.1}>
                <h2 style={{
                  fontFamily: "'Playfair Display', serif",
                  fontSize: 'clamp(30px, 4vw, 48px)', fontWeight: 700,
                  color: 'white', lineHeight: 1.15, marginTop: '14px',
                }}>
                  Four Revenue <span style={{ color: C.gold }}>Streams</span>
                </h2>
              </Reveal>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '20px' }}>
              {[
                { num: '01', title: 'Vendor Commissions', desc: 'Starter (Free): 8% per booking. Pro (R199/mo): 3%. Premium (R499/mo): 0%. Vendors pay as they grow.', icon: '💼', accent: C.coral },
                { num: '02', title: 'Gift Code Affiliate', desc: '5% commission on every gift purchased through our retail partner code system (GGR-XX-XXXXX format).', icon: '🎁', accent: C.gold },
                { num: '03', title: 'Sponsor Advertising', desc: 'Premium ad slots on the platform for event-related businesses. Monthly subscription model.', icon: '📢', accent: '#3B82F6' },
                { num: '04', title: 'Premium Plans', desc: 'Enhanced features for power users — priority vendor matching, custom branding, analytics.', icon: '⭐', accent: '#8B5CF6' },
              ].map((r, i) => (
                <Reveal key={i} delay={0.1 + i * 0.08}>
                  <div style={{
                    padding: '32px 24px', borderRadius: '16px',
                    background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)',
                    position: 'relative', overflow: 'hidden',
                  }}>
                    <div style={{
                      position: 'absolute', top: '-10px', right: '-10px',
                      fontSize: '80px', fontFamily: "'Playfair Display', serif",
                      fontWeight: 800, color: `${r.accent}08`, lineHeight: 1,
                    }}>{r.num}</div>
                    <div style={{ fontSize: '32px', marginBottom: '16px' }}>{r.icon}</div>
                    <h3 style={{ fontSize: '17px', fontWeight: 700, color: r.accent, marginBottom: '8px' }}>{r.title}</h3>
                    <p style={{ fontSize: '13px', lineHeight: 1.7, color: 'rgba(255,255,255,0.45)' }}>{r.desc}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        {/* ══════════════════════════════════════
            SLIDE 6: TECH STACK
            ══════════════════════════════════════ */}
        <section className="brochure-slide" style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: '80px 32px',
          background: C.cream,
        }}>
          <div style={{ maxWidth: '1000px', width: '100%' }}>
            <div style={{ textAlign: 'center', marginBottom: '48px' }}>
              <Reveal>
                <span style={{ fontSize: '12px', fontWeight: 700, color: C.charcoal, letterSpacing: '3px', textTransform: 'uppercase', opacity: 0.4 }}>Technology</span>
              </Reveal>
              <Reveal delay={0.1}>
                <h2 style={{
                  fontFamily: "'Playfair Display', serif",
                  fontSize: 'clamp(30px, 4vw, 48px)', fontWeight: 700,
                  color: C.charcoal, lineHeight: 1.15, marginTop: '14px',
                }}>
                  Built with <span style={{ color: C.coral }}>Modern Tech</span>
                </h2>
              </Reveal>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
              {[
                { label: 'Frontend', tech: 'React.js', desc: 'Fast, responsive single-page application', color: '#61DAFB' },
                { label: 'Backend', tech: 'Node.js + Express', desc: 'RESTful API with JWT authentication', color: '#68A063' },
                { label: 'Database', tech: 'PostgreSQL', desc: 'Robust relational database for data integrity', color: '#336791' },
                { label: 'AI Engine', tech: 'GiGi NLP', desc: 'Custom AI with multilingual SA language support', color: C.coral },
                { label: 'Messaging', tech: 'WhatsApp API', desc: 'Automated guest notifications and reminders', color: '#25D366' },
                { label: 'Hosting', tech: 'Cloud VPS', desc: 'PM2 process management, daily automated backups', color: '#FF9900' },
              ].map((t, i) => (
                <Reveal key={i} delay={0.05 + i * 0.06}>
                  <div style={{
                    padding: '24px', borderRadius: '14px',
                    background: 'white', border: '1px solid rgba(0,0,0,0.06)',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.03)',
                  }}>
                    <div style={{ fontSize: '11px', fontWeight: 700, color: t.color, letterSpacing: '2px', textTransform: 'uppercase', marginBottom: '8px' }}>{t.label}</div>
                    <h3 style={{ fontSize: '18px', fontWeight: 700, color: C.charcoal, marginBottom: '6px' }}>{t.tech}</h3>
                    <p style={{ fontSize: '13px', color: '#999', lineHeight: 1.5 }}>{t.desc}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        {/* ══════════════════════════════════════
            SLIDE 7: IMPACT & VISION
            ══════════════════════════════════════ */}
        <section className="brochure-slide" style={{
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          padding: '80px 32px', textAlign: 'center',
          background: `radial-gradient(ellipse at 50% 30%, rgba(201,168,76,0.1) 0%, transparent 60%),
                       ${C.charcoal}`,
        }}>
          <Reveal>
            <span style={{ fontSize: '12px', fontWeight: 700, color: C.gold, letterSpacing: '3px', textTransform: 'uppercase' }}>Our Vision</span>
          </Reveal>

          <Reveal delay={0.1}>
            <h2 style={{
              fontFamily: "'Playfair Display', serif",
              fontSize: 'clamp(30px, 5vw, 56px)', fontWeight: 700,
              color: 'white', lineHeight: 1.15, marginTop: '20px',
              maxWidth: '800px',
            }}>
              Empowering <span style={{ color: C.coral, fontStyle: 'italic' }}>every South African</span> to host unforgettable celebrations
            </h2>
          </Reveal>

          <Reveal delay={0.2}>
            <p style={{
              fontSize: '17px', lineHeight: 1.8, color: 'rgba(255,255,255,0.45)',
              maxWidth: '600px', marginTop: '24px',
            }}>
              Grand Gather Registry isn't just an app — it's a movement to digitise and preserve
              South Africa's rich celebration culture. From traditional lobola negotiations to modern
              corporate events, we bring communities together through technology.
            </p>
          </Reveal>

          <Reveal delay={0.3}>
            <div style={{
              display: 'flex', gap: '48px', marginTop: '56px', flexWrap: 'wrap', justifyContent: 'center',
            }}>
              <StatBlock number="12" label="Event Types" color="white" />
              <StatBlock number="7" label="SA Languages" color="white" />
              <StatBlock number="20+" label="Smart Features" color="white" />
              <StatBlock number="4" label="Revenue Streams" color="white" />
            </div>
          </Reveal>

          <Reveal delay={0.4}>
            <div style={{
              marginTop: '56px', padding: '20px 40px', borderRadius: '14px',
              background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)',
              display: 'inline-flex', flexDirection: 'column', alignItems: 'center',
            }}>
              <img src="/gift-icon-v2.png" alt="GGR" style={{ height: '48px', marginBottom: '12px', opacity: 0.8 }} />
              <div style={{ fontFamily: "'Playfair Display', serif", fontSize: '20px', color: 'white', fontWeight: 600 }}>Grand Gather Registry</div>
              <div style={{ fontSize: '13px', color: 'rgba(255,255,255,0.35)', marginTop: '4px' }}>Plan. Register. Celebrate.</div>
              <div style={{ fontSize: '12px', color: C.coral, marginTop: '12px', fontWeight: 600 }}>grandgatherregistry.co.za</div>
              <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.25)', marginTop: '4px' }}>info@grandgatherregistry.co.za · +27 61 391 1942</div>
              <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.15)', marginTop: '8px' }}>Made with ♥ · South Africa</div>
            </div>
          </Reveal>
        </section>

      </div>
    </div>
  );
}
